'use client'

import { Avatar, AvatarFallback, cn, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import React from 'react'

import type { ChainId } from '../web3.types'

import { getChainConfig } from '../utils'
import * as ChainIcons from './icons/chains'

interface ChainIconProps {
  /** Chain id for which to render an icon */
  chainId: ChainId | number | string
  className?: string
  /** When true, wraps the icon in a tooltip showing the chain short name */
  showTooltip?: boolean
}

function getChainFirstLetter(chainId: ChainId | number | string): string {
  const id = typeof chainId === 'number' ? chainId : Number(chainId)
  const chainName = getChainConfig(id)?.shortName ?? ''
  return chainName?.charAt(0) ?? 'C'
}

function FallbackChainIcon({ chainId, className }: ChainIconProps) {
  return (
    <Avatar className={cn('h-4 w-4 rounded-full', className)}>
      <AvatarFallback>{getChainFirstLetter(chainId)}</AvatarFallback>
    </Avatar>
  )
}

function getIconComponent(iconName: string): React.ComponentType<ChainIconProps> | undefined {
  const iconKey = `${iconName.replace(/\s+/g, '')}Icon` as keyof typeof ChainIcons
  // oxlint-disable-next-line import/namespace -- lookup key is derived at runtime
  return ChainIcons[iconKey] as React.ComponentType<ChainIconProps> | undefined
}

export function ChainIcon({ chainId, className, showTooltip }: ChainIconProps) {
  const id = typeof chainId === 'number' ? chainId : Number(chainId)
  const config = getChainConfig(id)
  const iconName = config?.shortName ?? ''

  let icon: React.ReactElement

  if (!iconName) {
    icon = <FallbackChainIcon chainId={chainId} className={cn('h-4 w-4 rounded-full', className)} />
  } else {
    const IconComponent = getIconComponent(iconName)

    if (!IconComponent) {
      icon = (
        <FallbackChainIcon chainId={chainId} className={cn('h-4 w-4 rounded-full', className)} />
      )
    } else {
      const isSquare = (config as { isIconSquare?: boolean }).isIconSquare

      icon = (
        <IconComponent
          chainId={chainId}
          className={cn('h-4 w-4', className, { 'rounded-full': !isSquare })}
        />
      )
    }
  }

  if (!showTooltip) {
    return icon
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span>{icon}</span>
      </TooltipTrigger>
      <TooltipContent>{config?.shortName ?? 'Unknown'}</TooltipContent>
    </Tooltip>
  )
}
